import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, ChevronRight, Download, Search, X, Zap } from "lucide-react";
import { useMemo, useState } from "react";

import {
  descargarActivacionStrikeXlsx,
  getActivacionStrike,
  getContratos,
  type ActivacionDia,
  type ActivacionStrike,
} from "@/lib/api";

const num = (n: number | null | undefined, d = 2) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("es-MX", { minimumFractionDigits: d, maximumFractionDigits: d }).format(n);

const hh = (h: number | null | undefined) =>
  h == null ? "—" : `${String(h).padStart(2, "0")}:00`;

const iso = (d: Date) => d.toISOString().slice(0, 10);

// Valor del combo para "todos los contratos" (el backend lo entiende como '*').
const TODOS = "*";

// Reporte por rango de fechas: en qué días y horas se ACTIVÓ el strike de la
// TBFin (Call-Option: PML_MDA >= strike; Take-or-Pay: las 24 h por contrato).
export function StrikeActivacionPage() {
  const hoy = new Date();
  // Default "Todos los contratos" (pedido del usuario 2026-09-01).
  const [contrato, setContrato] = useState<string>(TODOS);
  // Filtro por MES DE OPERACIÓN (pedido del usuario): un solo input type=month.
  const [ym, setYm] = useState(
    `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`,
  );
  const [bajando, setBajando] = useState(false);
  const [errorXlsx, setErrorXlsx] = useState<string | null>(null);

  // El mes se traduce a [primer día, último día]; el mes EN CURSO se recorta a
  // hoy (los días futuros no tienen PML/HSC y solo generarían filas de error).
  const rango = useMemo(() => {
    if (!/^\d{4}-\d{2}$/.test(ym)) return null;
    const [a, m] = ym.split("-").map(Number);
    const desde = iso(new Date(a, m - 1, 1));
    const finMes = new Date(a, m, 0); // día 0 del mes siguiente = último del mes
    const hasta = iso(finMes > hoy ? hoy : finMes);
    return hasta >= desde ? { desde, hasta } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ym]);

  // Solo contratos de ENERGÍA (mismo criterio que /tbfin: las fichas de
  // potencia/CEL no tienen motor TBFin ni nodo PML real).
  const contratosQ = useQuery({ queryKey: ["contratos"], queryFn: getContratos });
  const contratos = useMemo(
    () => (contratosQ.data?.items ?? []).filter((c) => (c.productos ?? []).includes("Energía")),
    [contratosQ.data],
  );

  // La consulta calcula en vivo los días sin snapshot (puede tardar varios
  // segundos; con "Todos" bastantes más): solo dispara al presionar Buscar.
  const [aplicado, setAplicado] = useState<{ contrato: string; desde: string; hasta: string } | null>(
    null,
  );
  const q = useQuery({
    queryKey: ["tbfin-activacion", aplicado?.contrato, aplicado?.desde, aplicado?.hasta],
    queryFn: () => getActivacionStrike(aplicado!.contrato, aplicado!.desde, aplicado!.hasta),
    enabled: !!aplicado,
    retry: false,
  });
  const reportes: ActivacionStrike[] = useMemo(() => {
    if (!q.data) return [];
    return q.data.multiple ? q.data.reportes : [q.data];
  }, [q.data]);

  const esTodos = contrato === TODOS;
  const filtrosListos = !!contrato && !!rango;
  const filtrosCambiaron =
    !!aplicado && !!rango &&
    (aplicado.contrato !== contrato ||
      aplicado.desde !== rango.desde ||
      aplicado.hasta !== rango.hasta);

  const buscar = () => {
    if (!filtrosListos || !rango) return;
    setErrorXlsx(null);
    setAplicado({ contrato, desde: rango.desde, hasta: rango.hasta });
  };

  const descargar = async () => {
    if (!aplicado) return;
    setBajando(true);
    setErrorXlsx(null);
    try {
      // Descarga EXACTAMENTE el corte buscado (no los filtros a medio editar).
      await descargarActivacionStrikeXlsx(aplicado.contrato, aplicado.desde, aplicado.hasta);
    } catch (e) {
      setErrorXlsx(e instanceof Error ? e.message : "No se pudo generar el Excel");
    } finally {
      setBajando(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <Zap className="w-5 h-5 text-slate-500" /> Activación de strike (TBFin)
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Elige el contrato (o todos) y el mes de operación: el reporte muestra <b>cuándo se
          activó el strike</b> — por día, las horas en que el PML del MDA alcanzó el strike
          (Call-Option) y su volumen; expande un día para ver el detalle hora por hora.
          Los días confirmados/enviados usan el snapshot congelado; el resto se calcula en vivo.
        </p>
      </div>

      <div className="bg-white border rounded-lg px-4 py-3 flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Contrato</span>
          <select
            className="mt-1 border rounded px-2 py-1.5 text-sm min-w-[260px]"
            value={contrato}
            onChange={(e) => setContrato(e.target.value)}
            disabled={contratos.length === 0}
          >
            <option value={TODOS}>— Todos los contratos —</option>
            {contratos.map((c) => (
              <option key={c.folio} value={c.folio}>
                {c.folio} — {c.contraparte}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Mes de operación</span>
          <input
            type="month"
            className="mt-1 border rounded px-2 py-1.5 text-sm"
            value={ym}
            onChange={(e) => setYm(e.target.value)}
          />
        </label>
        <button
          type="button"
          onClick={buscar}
          disabled={!filtrosListos || q.isFetching}
          title={filtrosListos ? undefined : "Elige contrato y mes de operación"}
          className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Search className="w-4 h-4" /> {q.isFetching ? "Calculando…" : "Buscar"}
        </button>
        <button
          type="button"
          onClick={descargar}
          disabled={bajando || q.isFetching || reportes.length === 0}
          title={aplicado ? "Descarga el corte buscado (hoja Resumen + hoja por contrato)" : "Primero presiona Buscar"}
          className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-4 h-4" /> {bajando ? "Generando…" : "Descargar Excel"}
        </button>
        <span className="text-[11px] text-slate-400 pb-2 ml-auto">
          {esTodos && "con Todos: un reporte por contrato, recortado a su vigencia · "}
          el mes en curso se corta en hoy · los días sin snapshot se calculan en vivo
        </span>
      </div>

      {filtrosCambiaron && !q.isFetching && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded p-3 text-sm">
          Cambiaste los filtros — se sigue mostrando{" "}
          {aplicado!.contrato === TODOS ? "todos los contratos" : aplicado!.contrato} del{" "}
          {aplicado!.desde} al {aplicado!.hasta}. Presiona <b>Buscar</b> para actualizar.
        </div>
      )}
      {q.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {(q.error as Error)?.message ?? "No se pudo consultar el reporte."}
        </div>
      )}
      {errorXlsx && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {errorXlsx}
        </div>
      )}
      {q.isFetching && (
        <div className="bg-white border rounded-lg px-4 py-6 text-sm text-slate-500">
          Calculando{aplicado?.contrato === TODOS ? " todos los contratos (puede tardar un rato)" : ""}…
        </div>
      )}

      {reportes.map((rep) => (
        <ReporteBloque key={rep.contrato} rep={rep} />
      ))}
      {q.data && reportes.length === 0 && (
        <div className="bg-white border rounded-lg px-4 py-6 text-sm text-slate-500">
          Ningún contrato con motor TBFin vive en el rango elegido.
        </div>
      )}

      {!aplicado && !q.isFetching && (
        <div className="bg-white border rounded-lg px-4 py-6 text-sm text-slate-500">
          Elige el contrato y el mes de operación, y presiona <b>Buscar</b>.
        </div>
      )}
    </div>
  );
}

function CeldasHoras({ d }: { d: ActivacionDia }) {
  return (
    <div className="flex gap-[2px]">
      {(d.horas ?? []).map((h) => (
        <span
          key={h.hora}
          title={`${hh(h.hora)} · PML ${h.pml == null ? "sin dato" : `$${num(h.pml)}`}${
            h.activada ? " · ACTIVADA" : ""
          }`}
          className={`inline-block w-2.5 h-4 rounded-[2px] ${
            h.activada ? "bg-emerald-500" : h.pml == null ? "bg-slate-100" : "bg-slate-300"
          }`}
        />
      ))}
    </div>
  );
}

function Kpi({ valor, etiqueta }: { valor: string; etiqueta: string }) {
  return (
    <div className="bg-white border rounded-lg px-4 py-3">
      <div className="text-lg font-semibold text-slate-900 tabular-nums">{valor}</div>
      <div className="text-[11px] text-slate-500 uppercase tracking-wide">{etiqueta}</div>
    </div>
  );
}

// Fila de un día + su detalle hora por hora al expandir: PML vs strike,
// PML − strike (qué tan dentro/fuera del dinero), MWh y monto por hora.
function DiaConHoras({ d, open, onToggle }: {
  d: ActivacionDia; open: boolean; onToggle: () => void;
}) {
  return (
    <>
      <tr
        className={`border-b last:border-0 hover:bg-slate-50 cursor-pointer ${
          (d.horas_activas ?? 0) === 0 ? "text-slate-400" : ""
        } ${open ? "bg-slate-50" : ""}`}
        onClick={onToggle}
      >
        <td className="px-2 py-1.5 text-slate-400">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </td>
        <td className="px-3 py-1.5 tabular-nums">{d.fecha}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{num(d.strike, 4)}</td>
        <td
          className={`px-3 py-1.5 text-right tabular-nums font-semibold ${
            (d.horas_activas ?? 0) > 0 ? "text-emerald-700" : ""
          }`}
        >
          {d.horas_activas} / 24
        </td>
        <td className="px-3 py-1.5 tabular-nums">
          {(d.horas_activas ?? 0) > 0 ? `${hh(d.primera_hora)} → ${hh(d.ultima_hora)}` : "—"}
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums">{num(d.mwh, 1)}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{num(d.pml_max)}</td>
        <td className="px-3 py-1.5">
          <CeldasHoras d={d} />
        </td>
        <td className="px-3 py-1.5 text-xs text-slate-500">
          {d.fuente === "snapshot" ? d.estado : "vivo"}
        </td>
      </tr>
      {open && (
        <tr className="border-b bg-slate-50/60">
          <td colSpan={9} className="px-6 py-3">
            <table className="text-xs w-full max-w-3xl">
              <thead className="text-slate-500">
                <tr className="border-b">
                  <th className="px-2 py-1 text-right">Hora</th>
                  <th className="px-2 py-1 text-right">PML MDA</th>
                  <th className="px-2 py-1 text-right">Strike</th>
                  <th className="px-2 py-1 text-right">PML − strike</th>
                  <th className="px-2 py-1 text-right">MWh</th>
                  <th className="px-2 py-1 text-right">Monto (MXN)</th>
                  <th className="px-2 py-1 text-center">Activada</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {(d.horas ?? []).map((h) => {
                  const dif = h.pml != null && d.strike != null ? h.pml - d.strike : null;
                  return (
                    <tr
                      key={h.hora}
                      className={`border-b last:border-0 ${
                        h.activada ? "bg-emerald-50/70" : "text-slate-400"
                      }`}
                    >
                      <td className="px-2 py-1 text-right">{hh(h.hora)}</td>
                      <td className="px-2 py-1 text-right">{num(h.pml)}</td>
                      <td className="px-2 py-1 text-right">{num(d.strike, 4)}</td>
                      <td
                        className={`px-2 py-1 text-right ${
                          dif != null && dif >= 0 ? "text-emerald-700 font-medium" : ""
                        }`}
                      >
                        {dif != null ? num(dif) : "—"}
                      </td>
                      <td className="px-2 py-1 text-right">{h.activada ? num(h.mwh, 3) : "—"}</td>
                      <td className="px-2 py-1 text-right">{h.activada ? num(h.total) : "—"}</td>
                      <td className="px-2 py-1 text-center">
                        {h.activada ? (
                          <Check className="w-3.5 h-3.5 inline text-emerald-600" />
                        ) : (
                          <X className="w-3.5 h-3.5 inline text-slate-300" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

function ReporteBloque({ rep }: { rep: ActivacionStrike }) {
  // Nivel 2: día expandido → tabla hora por hora (PML vs strike, MWh, monto).
  const [abierta, setAbierta] = useState<string | null>(null);
  return (
    <div className="space-y-3">
      {rep.take_or_pay && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
          <b>{rep.contrato}</b> es <b>Take-or-Pay</b>: el strike se activa las 24 horas por
          contrato, sin condicionarse al PML. Las celdas muestran de todas formas el PML
          horario contra el strike como referencia.
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi
          valor={`${rep.resumen.dias_con_activacion} / ${rep.resumen.dias_totales}`}
          etiqueta="días con activación"
        />
        <Kpi valor={num(rep.resumen.horas_activadas, 0)} etiqueta="horas activadas" />
        <Kpi valor={num(rep.resumen.mwh_total, 1)} etiqueta="MWh activados" />
        <Kpi
          valor={
            rep.resumen.primera_activacion
              ? `${rep.resumen.primera_activacion.fecha} ${hh(rep.resumen.primera_activacion.hora)}`
              : "—"
          }
          etiqueta="primera activación"
        />
        <Kpi
          valor={
            rep.resumen.ultima_activacion
              ? `${rep.resumen.ultima_activacion.fecha} ${hh(rep.resumen.ultima_activacion.hora)}`
              : "—"
          }
          etiqueta="última activación"
        />
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 border-b flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
          <span>
            <b className="text-slate-700">{rep.contrato}</b> · {rep.contraparte}
          </span>
          <span>Nodo {rep.nodo_pml}</span>
          <span>{rep.modalidad || "sin modalidad"}</span>
          {rep.automatica && (
            <span
              className="inline-block text-[10px] font-medium rounded-full px-1.5 py-0.5 bg-violet-100 text-violet-700"
              title="TBFin automática: MPP no la envía — la formaliza CENACE; el cálculo mostrado es el modelo del motor"
            >
              automática
            </span>
          )}
          <span>
            {rep.desde} → {rep.hasta}
          </span>
          <span className="ml-auto flex items-center gap-2">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-[2px] bg-emerald-500" /> hora
              activada
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-[2px] bg-slate-300" /> no activada
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-[2px] bg-slate-100 border" /> sin
              PML
            </span>
          </span>
        </div>
        <div className="overflow-x-auto max-h-[65vh]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 shadow-[0_1px_0_0_#e2e8f0]">
              <tr className="text-xs text-slate-500">
                <th className="px-2 py-2 w-6" />
                <th className="px-3 py-2 text-left">Fecha</th>
                <th className="px-3 py-2 text-right">Strike (MXN/MWh)</th>
                <th className="px-3 py-2 text-right">Horas activadas</th>
                <th className="px-3 py-2 text-left">Primera → última</th>
                <th className="px-3 py-2 text-right">MWh</th>
                <th className="px-3 py-2 text-right">PML máx</th>
                <th className="px-3 py-2 text-left">Horas 01–24</th>
                <th className="px-3 py-2 text-left">Fuente</th>
              </tr>
            </thead>
            <tbody>
              {rep.dias.map((d) => {
                if (d.error) {
                  return (
                    <tr key={d.fecha} className="border-b last:border-0 bg-red-50/60">
                      <td />
                      <td className="px-3 py-1.5 tabular-nums">{d.fecha}</td>
                      <td colSpan={7} className="px-3 py-1.5 text-xs text-red-700">
                        {d.error}
                      </td>
                    </tr>
                  );
                }
                const open = abierta === d.fecha;
                return (
                  <DiaConHoras
                    key={d.fecha}
                    d={d}
                    open={open}
                    onToggle={() => setAbierta(open ? null : d.fecha)}
                  />
                );
              })}
              {rep.dias.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-6 text-sm text-slate-500">
                    Sin días en el rango.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
